import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { User, LogOut, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo-theorie-aanbieders.png";

const navItems = [
  { to: "/rijscholen", label: "Rijscholen" },
  { to: "/online-theorie", label: "Online Training" },
  { to: "/theorie-boeken", label: "Boeken" },
  { to: "/tweewielers", label: "Motor & Brommer" },
  { to: "/code95", label: "Code 95" },
  { to: "/taxi", label: "Taxi" },
  { to: "/cbr", label: "CBR" },
  { to: "/videos", label: "Video" },
  { to: "/blog", label: "Blog" },
  { to: "/reviews", label: "Reviews" },
  { to: "/nieuws", label: "Nieuws" },
  { to: "/verenigingen", label: "Vereniging" },
  { to: "/vacatures", label: "Vacatures" },
  { to: "/partners", label: "Partners" },
];

export const Header = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        checkAdminStatus(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    // Use the has_role database function to check admin status
    const { data: hasAdminRole, error } = await supabase
      .rpc('has_role', { _user_id: userId, _role: 'admin' });

    if (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return;
    }

    setIsAdmin(hasAdminRole || false);

    if (hasAdminRole) {
      fetchNewLeadsCount();
    }
  };

  const fetchNewLeadsCount = async () => {
    const { count } = await supabase
      .from("lead_assignments")
      .select("*", { count: "exact", head: true })
      .eq("notified", false);

    setNewLeadsCount(count || 0);
  };

  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel('lead-assignments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lead_assignments'
        },
        () => {
          setTimeout(() => {
            fetchNewLeadsCount();
          }, 0);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMobileMenuOpen(false);
    toast({
      title: "Uitgelogd",
      description: "Je bent succesvol uitgelogd.",
    });
    navigate("/");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const toggleMobileMenu = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setMobileMenuOpen((v) => !v);
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-[60] lg:hidden w-full max-w-[100vw]">
        {/* Constrain the whole bar to the same container width as the hero */}
        <div className="w-full px-4">
          <div className="bg-background border-b border-border/50 shadow-sm rounded-b-xl" style={{ WebkitTransform: 'translateZ(0)' }}>
            <div className="flex items-center justify-between h-14 w-full px-1">
              {/* Logo */}
              <Link to="/" className="flex items-center flex-shrink-0" onClick={closeMobileMenu}>
                <img src={logo} alt="Theorie Aanbieders" className="h-6 w-auto" width="120" height="24" />
              </Link>

              {/* Mobile Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {session ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleLogout}
                    aria-label="Uitloggen"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                      <Link to="/auth" aria-label="Inloggen">
                        <User className="w-4 h-4" />
                      </Link>
                    </Button>
                    <Button variant="hero" size="sm" className="text-xs px-2 h-7" asChild>
                      <Link to="/auth">Aanmelden</Link>
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className="h-8 w-8 pointer-events-auto"
                  onClick={toggleMobileMenu}
                  onTouchStart={(e) => e.stopPropagation()}
                  aria-label={mobileMenuOpen ? "Menu sluiten" : "Menu openen"}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-nav"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 top-14 z-[70]" onClick={(e) => e.stopPropagation()}>
            {/* Backdrop overlay - clickable to close */}
            <div
              className="absolute inset-0 bg-black/60"
              onClick={closeMobileMenu}
              aria-hidden="true"
            />

            {/* Menu container */}
            <div 
              className="absolute top-0 left-0 right-0 w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full px-4 pt-2">
                <nav 
                  id="mobile-nav" 
                  className="bg-white dark:bg-gray-900 border-2 border-border shadow-2xl rounded-xl max-h-[70vh] overflow-y-auto"
                >
                  <ul className="py-2">
                    {navItems.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={closeMobileMenu}
                          className="block px-4 py-3 text-foreground hover:bg-muted active:bg-muted/80 rounded-lg transition-colors font-medium"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    {isAdmin && (
                      <li>
                        <Link
                          to="/admin"
                          onClick={closeMobileMenu}
                          className="block px-4 py-3 text-foreground hover:bg-muted active:bg-muted/80 rounded-lg transition-colors font-medium flex items-center"
                        >
                          Admin
                          {newLeadsCount > 0 && (
                            <Badge variant="destructive" className="ml-2 px-2 py-0.5 text-xs">
                              {newLeadsCount}
                            </Badge>
                          )}
                        </Link>
                      </li>
                    )}
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Desktop Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border/50 shadow-sm hidden lg:block" style={{ WebkitTransform: 'translateZ(0)' }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex flex-row items-center gap-3">
              <img src={logo} alt="Theorie Aanbieders" className="h-10 w-auto" width="200" height="40" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="flex items-center gap-4 ml-6">
              <Link to="/rijscholen" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Rijscholen
              </Link>
              <Link to="/online-theorie" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium text-center leading-tight">
                Online<br />Training
              </Link>
              <Link to="/theorie-boeken" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Boeken
              </Link>
              <Link to="/tweewielers" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium text-center leading-tight">
                Motor &<br />Brommer
              </Link>
              <Link to="/code95" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Code95
              </Link>
              <Link to="/taxi" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Taxi
              </Link>
              <Link to="/cbr" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                CBR
              </Link>
              <Link to="/videos" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Video
              </Link>
              <Link to="/blog" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Blog
              </Link>
              <Link to="/reviews" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Reviews
              </Link>
              <Link to="/nieuws" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Nieuws
              </Link>
              <Link to="/verenigingen" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Vereniging
              </Link>
              <Link to="/vacatures" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Vacatures
              </Link>
              <Link to="/partners" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium">
                Partners
              </Link>
              {isAdmin && (
                <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary transition-smooth font-medium relative">
                  Admin
                  {newLeadsCount > 0 && (
                    <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                      {newLeadsCount}
                    </Badge>
                  )}
                </Link>
              )}
            </nav>

            {/* Desktop Actions */}
            <div className="flex items-center gap-3">
              {session ? (
                <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Uitloggen">
                  <LogOut className="w-5 h-5" />
                </Button>
              ) : (
                <>
                  <Button variant="outline" asChild>
                    <Link to="/auth">
                      <User className="w-4 h-4 mr-2" />
                      Inloggen
                    </Link>
                  </Button>
                  <Button variant="hero" asChild>
                    <Link to="/auth">Aanmelden</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
